import { useEffect, useState, type FormEvent } from 'react'
import { PortalDisclosure } from '@gamecodex/portal-ui'
import { SHELF, SHELVES, RATING_IS_DISABLED_REASON } from './catalog'
import { useShelve } from './useShelve'
import { useAuth } from './useAuth'
import AuthPanel from './AuthPanel'

type Panel = 'closed' | 'composer' | 'auth'

/**
 * rpgreek.com
 *
 * Catalog-shaped on purpose: shelves in the sidebar, a ranked list with weight
 * badges, a submission form. The bit is that the Council never actually rates
 * anything you submit — it catalogs your intent to be rated and moves on.
 *
 * Submitting requires a real Atlas account (see AuthPanel + useShelve's
 * `loginRequired`): the site is fiction, the registration gate in front of it
 * is not.
 */
export default function App() {
  const auth = useAuth()
  const shelve = useShelve()
  const { pending, verdict, degraded, error, loginRequired, submit, dismiss } = shelve
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [panel, setPanel] = useState<Panel>('closed')

  // A session earned mid-composer (just registered/logged in) moves straight
  // to the composer instead of leaving the user staring at a stale form.
  useEffect(() => {
    if (auth.session && panel === 'auth') setPanel('composer')
  }, [auth.session, panel])

  // The endpoint, not just this component, decides a session is stale — if it
  // says so mid-submit, re-open the real gate rather than a dead-end error.
  useEffect(() => {
    if (loginRequired) setPanel('auth')
  }, [loginRequired])

  const onOpen = () => setPanel(auth.session ? 'composer' : 'auth')
  const onClose = () => {
    setPanel('closed')
    dismiss()
    auth.dismissError()
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    void submit(title, body)
  }

  return (
    <div className="app">
      <header className="top">
        <span className="wordmark">RPGreek</span>
        <span className="tag">catalog everything. judge everything.</span>
        {auth.session && (
          <span className="who-signed-in">
            {auth.session.account.email} ·{' '}
            <button type="button" className="link" onClick={() => void auth.logout()}>
              Log out
            </button>
          </span>
        )}
        <button className="new" type="button" onClick={panel === 'closed' ? onOpen : onClose}>
          {panel === 'closed' ? 'Submit for shelving' : 'Cancel'}
        </button>
      </header>

      <main className="cols">
        <nav className="shelves">
          {SHELVES.map((s) => (
            <a key={s} href="#" onClick={(e) => e.preventDefault()}>
              {s}
            </a>
          ))}
          <PortalDisclosure
            portal="RPGreek"
            detail="Games shown here are fictional. Nothing you submit is stored or rated. A real GameCodex account is required to submit."
            className="disclosure"
          />
        </nav>

        <section className="feed">
          {panel === 'auth' && (
            <>
              {loginRequired && (
                <p className="err" role="alert">
                  Your session expired. Log back in to finish submitting.
                </p>
              )}
              <AuthPanel auth={auth} />
            </>
          )}

          {panel === 'composer' && (
            <form className="composer" onSubmit={onSubmit}>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Game title"
                maxLength={300}
                aria-label="Game title"
              />
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Pitch it. What is it, what does it weigh, why should it be shelved."
                rows={5}
                maxLength={4000}
                aria-label="Game pitch"
              />
              <div className="row">
                <button type="submit" disabled={pending || !body.trim()}>
                  {pending ? 'Weighing…' : 'Submit'}
                </button>
                <span className="hint">All submissions are reviewed by the Weight Council.</span>
              </div>
            </form>
          )}

          {verdict && (
            <div className="verdict" role="status">
              <span className="who">The Weight Council</span>
              <p>{verdict}</p>
              <div className="row">
                <button type="button" onClick={dismiss}>
                  Understood
                </button>
                {degraded && <span className="hint">Reviewed under standard guidance.</span>}
              </div>
            </div>
          )}

          {error && (
            <p className="err" role="alert">
              {error}
            </p>
          )}

          {SHELF.map((g) => (
            <article key={g.id} className={g.logged === false ? 'game unplayed' : 'game'}>
              <div className="weight">
                <span className="num">{g.weight.toFixed(2)}</span>
                <span className="label">weight</span>
              </div>
              <div className="body">
                <div className="meta">
                  #{g.rank} · {g.shelf} · {g.designer}
                </div>
                <h2>{g.title}</h2>
                <p>{g.blurb}</p>
                <div className="actions" title={RATING_IS_DISABLED_REASON}>
                  {g.ratings.toLocaleString()} ratings
                </div>
              </div>
            </article>
          ))}
        </section>
      </main>

      <footer className="site-footer">
        <a href="/legal/terms">Terms of Service</a>
        <a href="/legal/privacy">Privacy Policy</a>
        <a href="/legal/ai">AI Policy</a>
        <span className="copyright">© {new Date().getFullYear()} GameCodex</span>
      </footer>
    </div>
  )
}
